import { Box, Heading, HStack, Tooltip, useDisclosure } from '@chakra-ui/react';
import Layout from '../common/Layout';
import DataTableSearchAndFiltering from '../common/table/DataTableSearchAndFiltering';
import { useGetStatesQuery } from '@/app/services/nucleus';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BsPencilSquare } from 'react-icons/bs';
import { toPactPercent } from '@/utils/helpers';
import TableButton from '../common/table/TableButton';
import StateModal from './StateModal';
import { selectStateMap } from '../enum/enumSlice';

const StatesPage = () => {
  const { data: statesData, isLoading, isError, error } = useGetStatesQuery();
  const statesMap = useSelector(selectStateMap);
  const columnHelper = createColumnHelper();
  const stateModal = useDisclosure();
  const [selectedState, setSelectedState] = useState(null);

  // Reverse lookup object (abbreviation -> full name)
  const abbreviationToNameMap = useMemo(() => {
    if (!statesMap) return {};

    return Object.entries(statesMap).reduce((acc, [fullName, abbreviation]) => {
      acc[abbreviation] = fullName;
      return acc;
    }, {});
  }, [statesMap]);

  const getStateFullName = (abbreviation) => {
    return abbreviationToNameMap[abbreviation];
  };

  const handleEditState = (state) => {
    setSelectedState({ ...state, displayName: getStateFullName(state.name) });
    stateModal.onOpen();
  };

  const stateColumns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => getStateFullName(info.getValue()),
    }),
    columnHelper.accessor('workers_compensation_rate', {
      header: "Worker's Comp",
      cell: (info) => {
        const value = info.getValue();
        return value ? `${toPactPercent(value, 2)}%` : '';
      },
    }),
    columnHelper.accessor('excise_tax', {
      header: 'Excise Tax',
      cell: (info) => {
        const value = info.getValue();
        return value ? `${toPactPercent(value, 2)}%` : '';
      },
    }),
    columnHelper.accessor('minimum_regular_pay_rate', {
      header: 'Minimum Pay Rate',
      cell: (info) => {
        const value = info.getValue();
        return value ? `$${value.toFixed(2)}` : '';
      },
    }),
    columnHelper.accessor('nlc_compact_status', {
      header: 'NLC Compact Status',
      name: 'nlc_compact_status',
    }),
    columnHelper.accessor('pt_compact_status', {
      header: 'PT Compact Status',
      name: 'pt_compact_status',
    }),
    columnHelper.accessor('ot_compact_status', {
      header: 'OT Compact Status',
      name: 'ot_compact_status',
    }),
    columnHelper.accessor('', {
      cell: (info) => {
        const state = info.row.original;
        return (
          <HStack>
            <Tooltip label="Edit State" aria-label="Edit State">
              <Box p="0" as="span">
                <TableButton aria-label="edit-state" icon={<BsPencilSquare />} onClick={() => handleEditState(state)} />
              </Box>
            </Tooltip>
          </HStack>
        );
      },
      header: 'Action',
    }),
  ];

  return (
    <Layout pageTitle={'Pact'}>
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        {'States'}
      </Heading>
      <DataTableSearchAndFiltering
        title="State Management"
        searchPlaceholder="Search states"
        originalData={statesData}
        dataColumns={stateColumns}
        isDataLoading={isLoading}
        isDataError={isError}
        dataErrorMsg={error?.data?.detail}
        dataErrorTitle={'Error fetching states'}
      />
      <StateModal isOpen={stateModal.isOpen} onClose={stateModal.onClose} state={selectedState} />
    </Layout>
  );
};

export default StatesPage;
